import { Wizard, type WizardStep } from '@/components/Wizard/Wizard'
import {
  ArrowRightOutlined,
  CheckOutlined,
  DownloadOutlined,
  UndoOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { useTenantsList } from '@api/hooks/useCrossroads'
import {
  type ChangeOwnershipResults,
  applyChangeOwnershipPlan,
  fetchChangeOwnershipPlan,
} from '@api/hooks/useTools'
import { GraviButton, Horizontal, Vertical } from '@gravitate-js/excalibrr'
import type { UploadFile } from 'antd'
import { Collapse, Divider, Form, Modal, Select, Upload, message } from 'antd'
import { useState } from 'react'

export function ChangeOwnershipPage() {
  const { data: tenants, isLoading: loadingTenants } = useTenantsList()
  const [currentOwner, setCurrentOwner] = useState<string>()
  const [newOwner, setNewOwner] = useState<string>()
  const [isDownloading, setIsDownloading] = useState(false)
  const [planFiles, setPlanFiles] = useState<UploadFile[]>([])
  const [isApplying, setIsApplying] = useState(false)
  const [applyResults, setApplyResults] = useState<ChangeOwnershipResults>()
  const [isReverting, setIsReverting] = useState(false)
  const [revertResults, setRevertResults] = useState<ChangeOwnershipResults>()

  const planFile = planFiles[0]?.originFileObj

  const tenantOptions =
    tenants?.flatMap((tenant) =>
      tenant.goid ? [{ value: tenant.goid, label: tenant.name }] : [],
    ) ?? []

  const filterOption = (
    input: string,
    option?: { value: string; label: string },
  ) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())

  const tenantNames = new Map(
    tenantOptions.map((option) => [option.value, option.label]),
  )

  const tenantName = (goid: string | undefined, fallback: string) =>
    (goid && tenantNames.get(goid)) || fallback

  const handleDownload = async () => {
    if (!currentOwner || !newOwner) {
      return
    }
    setIsDownloading(true)
    try {
      const blob = await fetchChangeOwnershipPlan(currentOwner, newOwner)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const currentName = tenantNames.get(currentOwner) ?? currentOwner
      const newName = tenantNames.get(newOwner) ?? newOwner
      link.setAttribute(
        'download',
        `change_ownership_${currentName}_to_${newName}.xlsx`,
      )
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Change ownership plan download failed:', error)
      message.error('Download failed. Please try again.')
    } finally {
      setIsDownloading(false)
    }
  }

  const applyPlan = async () => {
    if (!currentOwner || !newOwner || !planFile) {
      return
    }
    setIsApplying(true)
    try {
      const results = await applyChangeOwnershipPlan(
        currentOwner,
        newOwner,
        planFile,
      )
      setApplyResults(results)
      message.success('Ownership changes applied.')
    } catch (error) {
      console.error('Change ownership apply failed:', error)
      message.error('Applying changes failed. Please try again.')
    } finally {
      setIsApplying(false)
    }
  }

  const handleApply = () => {
    if (!currentOwner || !newOwner || !planFile) {
      return
    }
    Modal.confirm({
      title: 'Apply Ownership Changes',
      content: `Ownership of the master data in ${planFile.name} will be transferred from ${tenantName(currentOwner, 'the current owner')} to ${tenantName(newOwner, 'the new owner')}. Are you sure you want to continue?`,
      okText: 'Apply Changes',
      onOk: applyPlan,
    })
  }

  const revertPlan = async () => {
    if (!currentOwner || !newOwner || !planFile) {
      return
    }
    setIsReverting(true)
    try {
      const results = await applyChangeOwnershipPlan(
        currentOwner,
        newOwner,
        planFile,
        false,
      )
      setRevertResults(results)
      message.success('Ownership changes reverted.')
    } catch (error) {
      console.error('Change ownership revert failed:', error)
      message.error('Reverting changes failed. Please try again.')
    } finally {
      setIsReverting(false)
    }
  }

  const handleRevert = () => {
    if (!currentOwner || !newOwner || !planFile) {
      return
    }
    Modal.confirm({
      title: 'Undo Ownership Changes',
      content: `The migration in ${planFile.name} will be run in reverse, returning ownership from ${tenantName(newOwner, 'the new owner')} to ${tenantName(currentOwner, 'the current owner')}. Are you sure you want to continue?`,
      okText: 'Undo Changes',
      onOk: revertPlan,
    })
  }

  const resultsSummary = (results: ChangeOwnershipResults) => (
    <Vertical gap={4} style={{ marginTop: 16 }}>
      {Object.entries(results).map(([collection, result]) => (
        <span key={collection}>
          {collection}: {result.updated} updated, {result.skipped} skipped
        </span>
      ))}
    </Vertical>
  )

  const steps: WizardStep[] = [
    {
      title: 'Choose Tenants and Download Changes',
      explanation: (
        <>
          <p>
            Select the tenant you want to migrate master data from, then select
            the tenant that will become the new owner of the master data.
            Crossroads will build a worksheet that you can use to perform the
            migration.
          </p>
          <Collapse
            items={[
              {
                key: 'matching',
                label: 'How does Crossroads match data?',
                children: (
                  <p>
                    Crossroads will use the mappings between the tenants and
                    master data to determine how to transfer ownership. Sites in{' '}
                    {tenantName(currentOwner, 'the current owner')} that are
                    mapped to a site in {tenantName(newOwner, 'the new owner')}{' '}
                    can be automatically migrated. Tanks are matched in the same
                    way.
                  </p>
                ),
              },
            ]}
          />
        </>
      ),
      actions: (
        <Form layout='vertical' style={{ width: '100%' }}>
          <Horizontal gap={8} alignItems='flex-end'>
            <Form.Item label='From' style={{ flex: 1, marginBottom: 0 }}>
              <Select
                value={currentOwner}
                onChange={setCurrentOwner}
                placeholder='Select tenant'
                showSearch
                loading={loadingTenants}
                options={tenantOptions}
                filterOption={filterOption}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <ArrowRightOutlined style={{ marginBottom: 9 }} />
            <Form.Item label='To' style={{ flex: 1, marginBottom: 0 }}>
              <Select
                value={newOwner}
                onChange={setNewOwner}
                placeholder='Select tenant'
                showSearch
                loading={loadingTenants}
                options={tenantOptions}
                filterOption={filterOption}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Horizontal>
          <Form.Item style={{ marginBottom: 0, marginTop: 16 }}>
            <GraviButton
              theme2
              icon={<DownloadOutlined />}
              buttonText='Download Changes'
              onClick={handleDownload}
              disabled={!currentOwner || !newOwner}
              loading={isDownloading}
            />
          </Form.Item>
        </Form>
      ),
    },
    {
      title: 'Review Changes',
      explanation: (
        <>
          <p>
            Open the change worksheet and review the changes that will be made.
            The worksheet shows how each {tenantName(currentOwner, '')} master
            site and master tank will be edited. Crossroads will change certain
            fields on the master data to best reflect the updated ownership, but
            will <b>not</b> change the GOIDs. Data that was mapped to a master
            site or tank will stay mapped to that master site or tank after the
            ownership is transferred.
          </p>
          <Collapse
            items={[
              {
                key: 'everything',
                label: "What if I don't want to migrate everything?",
                children: (
                  <p>
                    You can delete rows from the changes worksheet if you don't
                    want the migration to take place. Be sure to also delete
                    tank rows to prevent them from migrating
                  </p>
                ),
              },
              {
                key: 'blanks',
                label: 'Some rows are blank?',
                children: (
                  <p>
                    If Crossroads could not determine how to change ownership,
                    it will leave the new_ fields blank. If you want to change
                    ownership of those rows, you can either add the data to the
                    sheet or you can map a {tenantName(newOwner, 'new owner')}{' '}
                    site to the master site and regenerate the worksheet.
                  </p>
                ),
              },
              {
                key: 'blanks2',
                label: 'Lots of rows are blank?',
                children: (
                  <p>
                    Crossroads uses the mappings between{' '}
                    {tenantName(newOwner, 'the new owner')} and master data. You
                    can reduce manual work by mapping more sites and tanks in{' '}
                    {tenantName(newOwner, 'the new owner')} to master data.
                  </p>
                ),
              },
              {
                key: 'fields',
                label: 'What are ready and multiple_matches?',
                children: (
                  <p>
                    These are convenience filters to help you edit the sheet.
                    Ready means that Crossroads was able to automatically change
                    ownership based on mapping, so you can filter out rows that
                    don't require edits. Multiple_matches is true when
                    Crossroads found multiple sites in{' '}
                    {tenantName(newOwner, 'the new owner')} for the site
                  </p>
                ),
              },
            ]}
          />
        </>
      ),
    },
    {
      title: 'Apply Changes',
      explanation: (
        <p>
          Attach your updated worksheet, then click Apply. Crossroads will
          execute the ownership change based on the data in the sheet.
        </p>
      ),
      actions: (
        <Form layout='vertical' style={{ width: '100%' }}>
          <Form.Item label='Updated worksheet'>
            <Upload
              accept='.xlsx'
              maxCount={1}
              beforeUpload={() => false}
              fileList={planFiles}
              onChange={({ fileList }) => setPlanFiles(fileList)}
            >
              <GraviButton
                icon={<UploadOutlined />}
                buttonText='Choose worksheet'
              />
            </Upload>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <GraviButton
              theme2
              icon={<CheckOutlined />}
              buttonText='Apply Changes'
              onClick={handleApply}
              disabled={!currentOwner || !newOwner || !planFile}
              loading={isApplying}
            />
          </Form.Item>
          {applyResults && resultsSummary(applyResults)}
        </Form>
      ),
    },
    {
      title: 'Optional: Revert Changes',
      explanation: (
        <p>
          Need to revert the changes? Crossroads can run the migration in
          reverse. Click Undo Changes to reset the master data to the state it
          had when you downloaded the sheet.
        </p>
      ),
      actions: (
        <Form layout='vertical' style={{ width: '100%' }}>
          <Form.Item style={{ marginBottom: 0 }}>
            <GraviButton
              icon={<UndoOutlined />}
              buttonText='Undo Changes'
              onClick={handleRevert}
              disabled={!applyResults || !planFile}
              loading={isReverting}
            />
          </Form.Item>
          {revertResults && resultsSummary(revertResults)}
        </Form>
      ),
    },
  ]

  return (
    <Vertical gap={16} height='100%'>
      <span>
        This wizard will help you migrate ownership of master data from one
        tenant to another.
      </span>
      <Divider style={{ margin: 0 }} />
      <Wizard steps={steps} />
    </Vertical>
  )
}
